import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { SHOTS, APP_URL } from "./data";
import { SectionHead } from "./Primitives";
import { useThemeMode, shotSrc } from "./Theme";

export const Showcase = () => {
  const [i, setI] = useState(0);
  const [spot, setSpot] = useState({ x: 50, y: 40 });
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 0.45], [10, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.45], [0.92, 1]);
  const s = SHOTS[i];
  const { dark } = useThemeMode();

  return (
    <section id="inside" ref={ref} data-testid="showcase-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Inside the workbench" title="The real app, page by page." body="Captured from the live Morphogenesis workbench. No mock-ups." />
        <div className="mt-12 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" role="tablist">
          {SHOTS.map((t, k) => (
            <button key={t.id} role="tab" aria-selected={k === i} onClick={() => setI(k)} data-testid={`showcase-tab-${t.id}`}
              className={`relative shrink-0 rounded-md px-4 py-2 text-sm transition-colors ${k === i ? "text-[var(--on-teal)]" : "text-[var(--muted)] hover:text-[var(--teal)]"}`}>
              {k === i && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-md bg-[var(--teal)]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative font-medium">{t.label}</span>
            </button>
          ))}
        </div>

        <div style={{ perspective: 1600 }} className="mt-8">
          <motion.div style={{ rotateX: rotate, scale }} className="origin-top">
            <div
              onPointerMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setSpot({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
              data-testid="showcase-frame"
              className="relative rounded-xl border border-[var(--line)] bg-[var(--surface)] p-2 sm:p-3 shadow-[0_50px_120px_-40px_rgba(17,25,23,0.35)]"
            >
              <div className="flex items-center gap-2 px-3 pb-2 sm:pb-3 pt-1">
                <span className="h-2.5 w-2.5 rounded-full bg-[var(--line)]" /><span className="h-2.5 w-2.5 rounded-full bg-[var(--line)]" /><span className="h-2.5 w-2.5 rounded-full bg-[var(--teal)]" />
                <span className="ml-3 font-mono text-[11px] sm:text-xs text-[var(--muted)] truncate">biio.replit.app{s.path}</span>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden rounded-lg bg-[var(--surface-2)]">
                <AnimatePresence initial={false}>
                  <motion.img
                    key={s.id + (dark ? "-d" : "")}
                    src={shotSrc(s.src, dark)}
                    alt={`${s.label} page of the Morphogenesis app`}
                    data-testid="showcase-image"
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 h-full w-full object-cover object-left-top"
                  />
                </AnimatePresence>
                <div className="pointer-events-none absolute inset-0 hidden md:block" style={{ background: `radial-gradient(520px circle at ${spot.x}% ${spot.y}%, transparent 0%, rgba(17,25,23,${dark ? 0.55 : 0.1}) 70%)` }} />
              </div>
            </div>
          </motion.div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p data-testid="showcase-note" className="text-[var(--muted)]"><span className="text-[var(--text)] font-medium">{s.label}.</span> {s.note}</p>
          <a href={`${APP_URL.replace(/\/$/, "")}${s.path}`} target="_blank" rel="noreferrer" data-testid="showcase-open-link" className="font-mono text-sm text-[var(--teal)] hover:text-[var(--text)] transition-colors">Open {s.label} →</a>
        </div>
      </div>
    </section>
  );
};
